import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { usePlan } from '../hooks/usePlan';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVehicles } from '../hooks/useVehicles';
import { useOwner } from '../hooks/useOwner';
import { Car, Search, Plus, Edit2, Trash2, Fuel, Calendar, Gauge, MapPin, ArrowUpDown, Check, X, Menu } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { MileageInput, MileageMismatchNotice } from '../components/common/MileageInput';
import { cropStyle } from '../utils/photoCrop';
import { isNative } from '../utils/native';
import { MakeBadge } from '../components/common/MakeBadge';
import { carPhotoFor } from '../utils/carPhotos';
import { StickyBar } from '../components/common/StickyBar';

// ─── Config ───────────────────────────────────────────────────────────────────
// Exported so other pages (VehicleDetail) can render the same human-readable
// status label instead of the raw DB value (e.g. "dokimi_test" -> "Dealer Plates").
export const STATUS_CONFIG = {
  active:      { label: 'Active',        color: '#22c55e', bg: '#22c55e15', border: '#22c55e30' },
  classic:     { label: 'Classic',       color: '#eab308', bg: '#eab30815', border: '#eab30830' },
  restoration: { label: 'Restoration',   color: '#f97316', bg: '#f9731615', border: '#f9731630' },
  service:     { label: 'In Service',    color: '#4f8ef7', bg: '#4f8ef715', border: '#4f8ef730' },
  inactive:    { label: 'Inactive',      color: '#6b7280', bg: '#6b728015', border: '#6b728030' },
  daneira:     { label: 'Company',       color: '#a855f7', bg: '#a855f715', border: '#a855f730' },
  immobilized: { label: 'Immobilized',   color: '#ef4444', bg: '#ef444415', border: '#ef444430' },
  dokimi_test: { label: 'Dealer Plates', color: '#06b6d4', bg: '#06b6d415', border: '#06b6d430' },
  service:     { label: 'Service',       color: '#3b82f6', bg: '#3b82f615', border: '#3b82f630' },
  sold:        { label: 'Sold',          color: '#f97316', bg: '#f9731615', border: '#f9731630' },
  scrapped:    { label: 'Scrapped',      color: '#dc2626', bg: '#dc262615', border: '#dc262630' },
};

const FUEL_COLORS = {
  Petrol: '#f97316', Diesel: '#6b7280', Electric: '#22c55e',
  Hybrid: '#06b6d4', LPG: '#a855f7', Other: '#8b5cf6',
};

const GRADIENTS = [
  ['#4f8ef7','#7c3aed'],
];

const FUEL_TYPES = ['Petrol','Diesel','Electric','Hybrid','LPG','Other'];
const STATUS_ORDER = ['active','classic','restoration','service','inactive','daneira','immobilized','dokimi_test','sold','scrapped'];
const BRANDS = ['Aston Martin','Audi','Bentley','BMW','Chrysler','Citroën','Dacia','Dodge','Fiat','Ford','Honda','Hyundai','Jaguar','Jeep','Kia','Land Rover','Lancia','Lexus','Mazda','Mercedes','Mini','Mitsubishi','Nissan','Opel','Peugeot','Porsche','Range Rover','Renault','Seat','Skoda','Subaru','Suzuki','Tesla','Toyota','Volkswagen','Volvo','Other'];
const OWNERSHIP = ['Owned','Leased','Rented','Company','Other'];

// Phone: one scrolling row of chips, with a thin line under it showing how far along it is.
const ScrollRow = ({ children }) => {
  const ref = useRef(null);
  const [m, setM] = useState({ left: 0, width: 100, fits: true });
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const next = scrollWidth <= clientWidth + 1
      ? { left: 0, width: 100, fits: true }
      : { left: Math.round((scrollLeft / scrollWidth) * 1000) / 10, width: Math.round((clientWidth / scrollWidth) * 1000) / 10, fits: false };
    setM(prev => (prev.left === next.left && prev.width === next.width && prev.fits === next.fits ? prev : next));
  }, []);
  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);
  useEffect(() => { update(); });
  return (
    <div className="m-scrollrow">
      <div className="m-chiprow" ref={ref} onScroll={update}>{children}</div>
      <div className="ci-track" aria-hidden="true">
        <div className="ci-thumb" style={{ left: `${m.left}%`, width: `${m.width}%` }} />
      </div>
    </div>
  );
};

const SORT_OPTIONS = [
  { key: 'default',    label: 'Default order',        short: 'Sort' },
  { key: 'value_desc', label: 'Value: high to low',   short: 'Value ↓' },
  { key: 'value_asc',  label: 'Value: low to high',   short: 'Value ↑' },
  { key: 'built_desc', label: 'Built: newest first',  short: 'Newest' },
  { key: 'built_asc',  label: 'Built: oldest first',  short: 'Oldest' },
];

// ─── Vehicle Card ─────────────────────────────────────────────────────────────
const VehicleCard = ({ vehicle: v, onOpen, onEdit, onDelete, canEdit, isOwner, isAdmin, index }) => {
  const statusCfg = STATUS_CONFIG[v.status] || STATUS_CONFIG.active;
  const fuelColor = FUEL_COLORS[v.fuel_type] || '#6b7280';
  const grad = GRADIENTS[index % GRADIENTS.length];

  return (
    <div
      onClick={onOpen}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        boxShadow: '0 4px 6px -1px rgba(79,142,247,0.1), 0 2px 4px -2px rgba(124,58,237,0.08)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-6px) scale(1.01)';
        e.currentTarget.style.boxShadow = `0 20px 40px rgba(79,142,247,0.2), 0 8px 16px rgba(124,58,237,0.15)`;
        e.currentTarget.style.borderColor = grad[0];
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0) scale(1)';
        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(79,142,247,0.1), 0 2px 4px -2px rgba(124,58,237,0.08)';
        e.currentTarget.style.borderColor = 'var(--border)';
      }}
    >
      {/* Photo / Gradient header — the PROFILE photo (is_primary), not just
          whichever photo happens to be first in the array; falls back to the
          first photo for older rows uploaded before is_primary existed. */}
      <div style={{ height: 180, position: 'relative', overflow: 'hidden' }}>
        {(v.vehicle_photos?.find(p => p.is_primary) || v.vehicle_photos?.[0]) ? (
          <img
            src={(v.vehicle_photos.find(p => p.is_primary) || v.vehicle_photos[0]).url}
            alt={v.model}
            style={{ width: '100%', height: '100%', ...cropStyle(v.vehicle_photos.find(p => p.is_primary) || v.vehicle_photos[0]) }}
          />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            background: `linear-gradient(135deg, ${grad[0]}33, ${grad[1]}55)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative',
          }}>
            {/* Grid pattern */}
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: `linear-gradient(${grad[0]}22 1px, transparent 1px), linear-gradient(90deg, ${grad[0]}22 1px, transparent 1px)`,
              backgroundSize: '20px 20px',
            }} />
            {/* Car icon circle */}
            <div style={{
              position: 'relative', width: 80, height: 80, borderRadius: '50%',
              background: `linear-gradient(135deg, ${grad[0]}44, ${grad[1]}44)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: `0 8px 32px ${grad[0]}44, inset 0 1px 0 rgba(255,255,255,0.2)`,
              border: `1px solid ${grad[0]}44`,
            }}>
              <Car size={36} style={{ color: grad[0], filter: `drop-shadow(0 2px 8px ${grad[0]}88)` }} />
            </div>
          </div>
        )}

        {/* Year badge */}
        {v.year && (
          <div style={{ position: 'absolute', top: 12, left: 12 }}>
            <span style={{
              padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
              background: 'rgba(0,0,0,0.5)', color: 'white',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}>
              {v.year}
            </span>
          </div>
        )}

        {/* Bottom fade */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 60, background: 'linear-gradient(transparent, rgba(0,0,0,0.4))' }} />
      </div>

      {/* Card body */}
      <div style={{ padding: '16px 18px 18px' }}>
        {/* Reg */}
        <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: grad[0], fontWeight: 600, marginBottom: 4, letterSpacing: 1 }}>
          {v.registration_number}
        </div>

        {/* Brand + Model */}
        <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: -0.5, marginBottom: 12, lineHeight: 1.2 }}>
          {v.brand}{' '}
          <span style={{
            background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            {v.model}
          </span>
        </div>

        {/* Stats */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            { icon: Fuel, label: v.fuel_type || '—', color: fuelColor },
            { icon: Gauge, label: v.mileage ? `${Number(v.mileage).toLocaleString()} km` : '—', color: 'var(--text3)' },
            { icon: Calendar, label: (v.status === 'daneira' && v.company_name) ? v.company_name : (v.ownership_status || '—'), color: 'var(--text3)' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <item.icon size={11} style={{ color: item.color, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>{item.label}</span>
            </div>
          ))}
        </div>

        {/* Where the car is right now */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: -4, marginBottom: 14, minWidth: 0 }} title={v.location || 'No location set'}>
          <MapPin size={11} style={{ color: v.location ? grad[0] : 'var(--text3)', flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: v.location ? 'var(--text2)' : 'var(--text3)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {v.location || 'Location not set'}
          </span>
        </div>

        {/* Gradient divider */}
        <div style={{ height: 1, background: `linear-gradient(90deg, ${grad[0]}44, transparent)`, marginBottom: 14 }} />

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
          <button
            onClick={onOpen}
            style={{
              flex: 1, padding: '8px', borderRadius: 8,
              border: `1px solid ${grad[0]}44`,
              background: `linear-gradient(135deg, ${grad[0]}11, ${grad[1]}11)`,
              color: grad[0], fontSize: 12, fontWeight: 700,
              cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`;
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.boxShadow = `0 4px 12px ${grad[0]}44`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = `linear-gradient(135deg, ${grad[0]}11, ${grad[1]}11)`;
              e.currentTarget.style.color = grad[0];
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            View Details
          </button>

          {canEdit && (
            <button
              onClick={onEdit}
              style={{
                padding: '8px 12px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--surface2)',
                color: 'var(--text2)', fontSize: 12, cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
            >
              <Edit2 size={13} />
            </button>
          )}

          {(isOwner || isAdmin) && (
            <button
              onClick={onDelete}
              style={{
                padding: '8px 12px', borderRadius: 8,
                border: '1px solid var(--red-bg)', background: 'var(--red-bg)',
                color: 'var(--red)', fontSize: 12, cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--red)'; e.currentTarget.style.color = 'white'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--red-bg)'; e.currentTarget.style.color = 'var(--red)'; }}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Phone car card: photo, status dot, brand badge, name, year and mileage ──────────
const PhoneVehicleCard = ({ vehicle: v, onOpen }) => {
  const photo = v.vehicle_photos?.find(p => p.is_primary) || v.vehicle_photos?.[0];
  const studio = carPhotoFor(v);                       // the studio picture, when this car has one
  const cfg = STATUS_CONFIG[v.status] || STATUS_CONFIG.active;
  const sub = [v.year, v.mileage ? `${Number(v.mileage).toLocaleString()} km` : null].filter(Boolean).join('  •  ');
  return (
    <button type="button" className="cx-card" onClick={onOpen} aria-label={`${v.brand || ''} ${v.model || ''}`.trim()}>
      {studio
        ? <img className="cx-card-img is-studio" src={studio} alt="" loading="lazy" style={{ width: '100%', height: '100%' }} />
        : photo
        ? <img className="cx-card-img" src={photo.url} alt="" style={{ width: '100%', height: '100%', ...cropStyle(photo) }} />
        : <span className="cx-card-empty"><Car size={46} /></span>}
      <span className="cx-card-shade" />
      <span className="cx-dot" style={{ '--dot': cfg.color }} title={cfg.label} />
      <span className="cx-card-info">
        <MakeBadge brand={v.brand} />
        <span className="cx-card-text">
          <span className="cx-card-name">{v.brand} {v.model}</span>
          {sub && <span className="cx-card-sub">{sub}</span>}
        </span>
      </span>
    </button>
  );
};

// ─── Vehicles Page ────────────────────────────────────────────────────────────
export const Vehicles = () => {
  const { vehicles, loading, deleteVehicle } = useVehicles();
  const { role, isOwner, isAdmin } = useOwner();
  const { plan, limits, canAddVehicle, vehicleLimit, isTrialing, trialDaysLeft, isReadOnly } = usePlan();
  const canEdit = isOwner || isAdmin || role === 'editor';
  const [search, setSearch] = useState('');
  const [fuelFilter, setFuelFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [sortSheet, setSortSheet] = useState(false);
  const categoryCount = new Set(vehicles.map(v => v.status || 'active')).size;
  const [searchOpen, setSearchOpen] = useState(false);   // phone: search field open in the filter bar
  const searchInput = useRef(null);
  const [valuations, setValuations] = useState({});   // vehicle_id -> { value, built }
  const navigate = useNavigate();

  // Value and build date live on the valuation record, not the vehicle, so they are
  // loaded separately — only to power the sort options.
  useEffect(() => {
    let cancelled = false;
    supabase.from('vehicle_valuations').select('vehicle_id, current_value, build_date').then(({ data }) => {
      if (cancelled || !data) return;
      const map = {};
      data.forEach(r => { map[r.vehicle_id] = { value: r.current_value ? Number(r.current_value) : null, built: r.build_date || null }; });
      setValuations(map);
    });
    return () => { cancelled = true; };
  }, []);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from the Dashboard's Fleet Status card: /vehicles?category=immobilized
  // scrolls straight to that status group instead of landing at the top of the page.
  const scrollCategory = searchParams.get('category');
  const scrolledRef = useRef(false);
  useEffect(() => {
    if (scrollCategory) {
      scrolledRef.current = false;
      // The grouped-by-status layout only renders when the dropdown filter is
      // "all" — a deep link should always show it, regardless of what was
      // last selected.
      setStatusFilter(isNative && STATUS_CONFIG[scrollCategory] ? scrollCategory : 'all');
    }
  }, [scrollCategory]);

  const filtered = vehicles.filter(v => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      v.registration_number?.toLowerCase().includes(q) ||
      v.brand?.toLowerCase().includes(q) ||
      v.model?.toLowerCase().includes(q);
    const matchFuel = !fuelFilter || v.fuel_type === fuelFilter;
    const matchStatus = statusFilter === 'all' || v.status === statusFilter;
    return matchSearch && matchFuel && matchStatus;
  });

  // Sorting applies inside each status group (and to the flat list when a status is
  // chosen). Cars with no value / no year always sink to the bottom, either direction.
  const valueOf = v => valuations[v.id]?.value ?? null;
  // Build date when the owner recorded one, otherwise the model year (as 1 Jan).
  const builtOf = v => valuations[v.id]?.built || (v.year ? `${v.year}-01-01` : null);
  const compareBy = (get, dir) => (a, b) => {
    const x = get(a), y = get(b);
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return (x < y ? -1 : x > y ? 1 : 0) * dir;
  };
  const SORTS = {
    value_desc: compareBy(valueOf, -1),
    value_asc:  compareBy(valueOf, 1),
    built_desc: compareBy(builtOf, -1),
    built_asc:  compareBy(builtOf, 1),
  };
  if (SORTS[sortBy]) filtered.sort(SORTS[sortBy]);

  // Group by status
  const grouped = {};
  filtered.forEach(v => {
    const s = v.status || 'active';
    if (!grouped[s]) grouped[s] = [];
    grouped[s].push(v);
  });

  const sortedGroups = STATUS_ORDER.filter(s => grouped[s]?.length > 0);

  // Scroll to the requested group once its data (and DOM node) actually exist.
  useEffect(() => {
    if (!scrollCategory || loading || scrolledRef.current) return;
    if (isNative) {
      // The phone shows one flat list, so there is no group to scroll to: just tidy the address.
      scrolledRef.current = true;
      setSearchParams(params => { const next = new URLSearchParams(params); next.delete('category'); return next; }, { replace: true });
      return;
    }
    const el = document.getElementById(`vehicle-group-${scrollCategory}`);
    if (!el) return; // group has no vehicles (or hasn't rendered yet) — nothing to scroll to
    scrolledRef.current = true;
    // Let the grouped layout finish painting before measuring its position.
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    // Drop ?category= once we've landed, so the URL is clean and changing the
    // status filter afterwards doesn't re-trigger this scroll.
    setSearchParams(params => {
      const next = new URLSearchParams(params);
      next.delete('category');
      return next;
    }, { replace: true });
  }, [scrollCategory, loading, sortedGroups.length, setSearchParams]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this vehicle and all its records?')) return;
    await deleteVehicle(id);
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div className="page">
      {/* Header (web only: on the phone the camera button opens Add vehicle / scan) */}
      {!isNative && <div className="page-header">
        <div>
          <div className="page-title" style={{ fontSize: 26, fontWeight: 900, letterSpacing: -0.5 }}>
            Fleet Vehicles
          </div>
          <div className="page-subtitle">
            {vehicles.length} vehicles · {Object.keys(grouped).length} categor{Object.keys(grouped).length === 1 ? 'y' : 'ies'}
          </div>
        </div>
        {canEdit && (
          <button
            className="btn btn-primary"
            onClick={() => {
              // Two different reasons the add can be refused — say which one.
              if (isReadOnly) {
                toast.error(isNative ? 'Your free trial has ended, so your fleet is read-only.' : 'Your free trial has ended. Choose a plan in Settings → Billing to add vehicles again.');
                return;
              }
              if (!canAddVehicle(vehicles.length)) {
                toast.error(isNative ? `Your ${plan} plan allows up to ${vehicleLimit} vehicles.` : `Your ${plan} plan allows up to ${vehicleLimit} vehicles. Upgrade in Settings → Billing.`);
                return;
              }
              navigate('/vehicles/new');
            }}
          >
            <Plus size={14} /> Add Vehicle
          </button>
        )}
      </div>}
      {/* Phone: "My Collection" header, then ONE line: status chips + Sort (pinned while you scroll) */}
      {isNative && (
        <>
          <div className="cx-head">
            <div className="cx-head-bg" aria-hidden="true" />
            <div className="cx-head-photo" aria-hidden="true" style={{ backgroundImage: "url('/brand/photos/collection-banner.jpeg')" }} />
            <div className="cx-head-fade" aria-hidden="true" />
            <div className="cx-head-row">
              <div className="cx-head-text">
                <h1 className="cx-title">My Collection</h1>
                <div className="cx-sub">
                  {vehicles.length} vehicle{vehicles.length === 1 ? '' : 's'}  •  {categoryCount} categor{categoryCount === 1 ? 'y' : 'ies'}
                </div>
              </div>
              <div className="cx-head-actions">
                <button
                  className="cx-circle"
                  aria-label="Search vehicles"
                  onClick={() => { setStatusFilter('all'); setSearchOpen(true); setTimeout(() => searchInput.current?.focus(), 60); }}
                >
                  <Search size={19} />
                </button>
                <button className="cx-circle" aria-label="Menu" onClick={() => navigate('/menu')}>
                  <Menu size={20} />
                </button>
              </div>
            </div>
          </div>
          <StickyBar className={`cx-bar${searchOpen ? ' is-searching' : ''}`}>
            {searchOpen ? (
              <>
                <div className="m-searchfield">
                  <Search size={18} />
                  <input
                    ref={searchInput}
                    type="search"
                    inputMode="search"
                    enterKeyHint="search"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    placeholder="Search reg, brand or model"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    aria-label="Search vehicles"
                  />
                  {search && (
                    <button className="m-searchclear" aria-label="Clear search" onClick={() => { setSearch(''); searchInput.current?.focus(); }}>
                      <X size={16} />
                    </button>
                  )}
                </div>
                <button className="m-searchdone" onClick={() => { setSearch(''); setSearchOpen(false); }}>Cancel</button>
              </>
            ) : (
              <>
                <ScrollRow>
                  {['all', ...STATUS_ORDER].map(s => {
                    const cfg = STATUS_CONFIG[s];
                    const count = s === 'all' ? vehicles.length : vehicles.filter(v => (v.status || 'active') === s).length;
                    if (s !== 'all' && count === 0) return null;
                    const isActive = statusFilter === s;
                    return (
                      <button
                        key={s}
                        className={`cx-chip ${isActive ? 'is-active' : ''}`}
                        onClick={() => setStatusFilter(s)}
                        ref={el => { if (el && isActive && el.scrollIntoView) el.scrollIntoView({ inline: 'center', block: 'nearest' }); }}
                      >
                        <span>{s === 'all' ? 'All' : cfg.label}</span>
                        <span className="cx-chip-count">{count}</span>
                      </button>
                    );
                  })}
                </ScrollRow>
                <button className={`cx-sort ${sortBy !== 'default' ? 'is-set' : ''}`} onClick={() => setSortSheet(true)} aria-label="Sort vehicles">
                  <ArrowUpDown size={18} />
                </button>
              </>
            )}
          </StickyBar>
          {sortSheet && createPortal(
            <div className="m-sheet-scrim" onClick={() => setSortSheet(false)}>
              <div className="m-sheet" onClick={e => e.stopPropagation()}>
                <div className="m-sheet-grip" />
                <div className="m-sheet-head">
                  <div className="m-sheet-title">Sort cars by</div>
                  <button className="m-iconbtn" aria-label="Close" onClick={() => setSortSheet(false)}><X size={22} /></button>
                </div>
                {SORT_OPTIONS.map(o => (
                  <button key={o.key} className={`m-sortrow ${sortBy === o.key ? 'is-on' : ''}`} onClick={() => { setSortBy(o.key); setSortSheet(false); }}>
                    <span>{o.label}</span>
                    {sortBy === o.key && <Check size={22} />}
                  </button>
                ))}
              </div>
            </div>,
            document.body
          )}
        </>
      )}

      {/* Plan banner */}
      {isTrialing && trialDaysLeft <= 7 && (
        <div style={{ background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 10, padding: '10px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#eab308' }}>⏳ Your free trial ends in <strong>{trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''}</strong></span>
          {!isNative && <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)', color: 'white', border: 'none', fontSize: 12 }} onClick={() => navigate('/settings')}>Upgrade now →</button>}
        </div>
      )}
      {isReadOnly && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#ef4444' }}>
            ⚠️ Your free trial has ended — your fleet is <strong>read-only</strong>. Everything is still here.
          </span>
          {!isNative && <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)', color: 'white', border: 'none', fontSize: 12 }} onClick={() => navigate('/settings')}>Choose a plan →</button>}
        </div>
      )}
      {plan === 'free' && !isTrialing && !isReadOnly && (
        <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '10px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#ef4444' }}>⚠️ Free plan — limited to <strong>{vehicleLimit} vehicles</strong></span>
          {!isNative && <button className="btn btn-sm" style={{ background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)', color: 'white', border: 'none', fontSize: 12 }} onClick={() => navigate('/settings')}>Upgrade →</button>}
        </div>
      )}
      {/* Filters (web) */}
      {!isNative && <div className="card" style={{ marginBottom: 24, padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text3)' }} />
            <input
              className="form-input"
              style={{ paddingLeft: 36 }}
              placeholder="Search by reg, brand, model…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          <select className="form-input" style={{ width: 'auto' }} value={fuelFilter} onChange={e => setFuelFilter(e.target.value)}>
            <option value="">All fuel types</option>
            {FUEL_TYPES.map(f => <option key={f}>{f}</option>)}
          </select>

          <select className="form-input" style={{ width: 'auto' }} value={sortBy} onChange={e => setSortBy(e.target.value)} aria-label="Sort vehicles">
            <option value="default">Sort: Default</option>
            <option value="value_desc">Value: high to low</option>
            <option value="value_asc">Value: low to high</option>
            <option value="built_desc">Built: newest first</option>
            <option value="built_asc">Built: oldest first</option>
          </select>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {['all', ...STATUS_ORDER].map(s => {
              const cfg = STATUS_CONFIG[s];
              const count = s === 'all' ? vehicles.length : (grouped[s]?.length || 0);
              if (s !== 'all' && count === 0) return null;
              const isActive = statusFilter === s;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
                    border: `1px solid ${isActive ? (cfg?.color || 'var(--accent)') : 'var(--border)'}`,
                    background: isActive ? (cfg?.bg || 'var(--accent-glow)') : 'var(--surface)',
                    color: isActive ? (cfg?.color || 'var(--accent)') : 'var(--text3)',
                    boxShadow: isActive ? `0 4px 12px ${cfg?.color || 'var(--accent)'}33` : 'none',
                    transform: isActive ? 'translateY(-1px)' : 'none',
                  }}
                >
                  {s === 'all' ? `All (${count})` : `${cfg.label} (${count})`}
                </button>
              );
            })}
          </div>
        </div>
      </div>}

      {/* Empty state */}
      {filtered.length === 0 && (
        <div className="empty-state">
          <Car />
          <h3>No vehicles found</h3>
          <p>{search ? 'Try a different search term' : 'Add your first vehicle to get started'}</p>
        </div>
      )}

      {/* Phone: one flat list of photo cards. Web: cards grouped by status. */}
      {isNative ? (
        <div className="cx-list">
          {filtered.map(v => <PhoneVehicleCard key={v.id} vehicle={v} onOpen={() => navigate(`/vehicles/${v.id}`)} />)}
        </div>
      ) : statusFilter === 'all' ? (
        sortedGroups.map(status => (
          <div key={status} id={`vehicle-group-${status}`} style={{ marginBottom: 36, scrollMarginTop: isNative ? 84 : 24 }}>
            {/* Group header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{
                width: 10, height: 10, borderRadius: '50%',
                background: STATUS_CONFIG[status]?.color,
                boxShadow: `0 0 10px ${STATUS_CONFIG[status]?.color}`,
              }} />
              <span style={{ fontWeight: 800, fontSize: 15, letterSpacing: -0.3 }}>
                {STATUS_CONFIG[status]?.label}
              </span>
              <span style={{
                fontSize: 14, color: 'var(--text2)',
                background: 'var(--surface2)', padding: '2px 10px',
                borderRadius: 20, border: '1px solid var(--border)',
              }}>
                {grouped[status].length} vehicle{grouped[status].length !== 1 ? 's' : ''}
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {grouped[status].map((v, i) => (
                <VehicleCard
                  key={v.id}
                  vehicle={v}
                  index={i}
                  onOpen={() => navigate(`/vehicles/${v.id}`)}
                  onEdit={e => { e.stopPropagation(); navigate(`/vehicles/${v.id}/edit`); }}
                  onDelete={(e) => handleDelete(e, v.id)}
                  canEdit={canEdit}
                  isOwner={isOwner}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {filtered.map((v, i) => (
            <VehicleCard
              key={v.id}
              vehicle={v}
              index={i}
              onOpen={() => navigate(`/vehicles/${v.id}`)}
              onEdit={e => { e.stopPropagation(); navigate(`/vehicles/${v.id}/edit`); }}
              onDelete={(e) => handleDelete(e, v.id)}
              canEdit={canEdit}
              isOwner={isOwner}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Vehicle Form ─────────────────────────────────────────────────────────────
export const VehicleForm = ({ vehicle, onSave, onCancel }) => {
  const { createVehicle, updateVehicle } = useVehicles();
  const [form, setForm] = useState({
    registration_number: vehicle?.registration_number || '',
    brand: vehicle?.brand || '',
    model: vehicle?.model || '',
    variant: vehicle?.variant || '',
    generation: vehicle?.generation || '',
    body_style: vehicle?.body_style || '',
    engine_size: vehicle?.engine_size || '',
    taxonomy_match_id: vehicle?.taxonomy_match_id || '',
    year: vehicle?.year || new Date().getFullYear(),
    color: vehicle?.color || '',
    vin: vehicle?.vin || '',
    engine_number: vehicle?.engine_number || '',
    fuel_type: vehicle?.fuel_type || 'Petrol',
    mileage: vehicle?.mileage || 0,
    ownership_status: vehicle?.ownership_status || 'Owned',
    status: vehicle?.status || 'active',
    location: vehicle?.location || '',
    company_name: vehicle?.company_name || '',
    notes: vehicle?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // The Collector Score keeps its own "Current Mileage"; compare it with this form's value.
  const [scoreKm, setScoreKm] = useState(null);
  useEffect(() => {
    if (!vehicle?.id) return;
    let live = true;
    supabase.from('vehicle_valuations').select('mileage_score')
      .eq('vehicle_id', vehicle.id).order('updated_at', { ascending: false }).limit(1)
      .then(({ data }) => { if (live) setScoreKm(data?.[0]?.mileage_score ?? null); });
    return () => { live = false; };
  }, [vehicle?.id]);

  // ── Taxonomy-driven selection ──────────────────────────────────────────────
  // Picking from the catalogue means the vehicle is stored with the SAME values
  // the comparables carry, so matching is exact rather than a text comparison.
  // Free text stays available: 63% of listings are makes outside the taxonomy,
  // and a customer with a Ferrari must not be blocked by a dropdown.
  const [taxonomy, setTaxonomy] = useState([]);
  const [taxLoading, setTaxLoading] = useState(true);
  const [freeText, setFreeText] = useState(false);   // catalogue first, always
  // A brand/model can be in the taxonomy while the specific variant isn't (a
  // limited run, a regional spec, a typo in the source data) — this lets the
  // customer type the variant by hand without dropping brand/model matching.
  const [manualVariant, setManualVariant] = useState(false);

  useEffect(() => {
    // Supabase caps a select at 1,000 rows by default. The taxonomy is ~2,650, so
    // a single select silently truncated it and later makes (Land Rover, Porsche,
    // Toyota…) lost most of their entries. Page through explicitly.
    let cancelled = false;
    (async () => {
      const PAGE = 1000;
      let all = [], from = 0;
      for (;;) {
        const { data, error } = await supabase
          .from('vehicle_taxonomy')
          .select('id, make, model, variant, generation, body_style, listings')
          .order('make').order('model').order('id')
          .range(from, from + PAGE - 1);
        if (error) { console.error('taxonomy load failed', error); break; }
        all = all.concat(data || []);
        if (!data || data.length < PAGE) break;
        from += PAGE;
      }
      if (!cancelled) { setTaxonomy(all); setTaxLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const taxMakes = useMemo(
    () => [...new Set(taxonomy.map(t => t.make))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [taxonomy]);

  // Source listings write the same model several ways — "(E30) M3" and "E30 M3",
  // "(R230) SL500" and "R230 SL500". Normalising for the KEY merges those into one
  // option; fractions are preserved as content so a Bentley 3 Litre and 3½ Litre
  // stay properly separate.
  const modelKey = (m) => (m || '')
    .replace(/½/g, ' 1/2').replace(/¼/g, ' 1/4').replace(/¾/g, ' 3/4')
    .replace(/⅓/g, ' 1/3').replace(/⅔/g, ' 2/3')
    .toLowerCase()
    .replace(/[()[\]]/g, ' ')
    .replace(/[^a-z0-9/]+/g, ' ')
    .trim().replace(/\s+/g, ' ');

  const taxModels = useMemo(() => {
    const agg = {};
    taxonomy.filter(t => t.make === form.brand).forEach(t => {
      const k = modelKey(t.model);
      if (!k) return;
      if (!agg[k]) agg[k] = { label: t.model, listings: 0, models: new Set() };
      agg[k].listings += (t.listings || 0);
      agg[k].models.add(t.model);
      // Show the tidiest spelling: prefer one without brackets, then the longest
      // (usually the most complete), so "(E30) M3" yields "E30 M3".
      const cur = agg[k].label, cand = t.model;
      const score = (x) => (/[()[\]]/.test(x) ? 0 : 2) + (x.length > cur.length ? 1 : 0);
      if (score(cand) > score(cur)) agg[k].label = cand;
    });
    return Object.values(agg)
      .map(v => ({ ...v, models: [...v.models] }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [taxonomy, form.brand]);

  // Variant + generation + body are chosen together — they only mean anything
  // as a combination, and offering them as three independent lists would let a
  // customer build a car that doesn't exist.
  const specLabel = (t) => [t.generation, t.variant, t.body_style]
    .filter(Boolean).join(' · ') || 'Standard';

  const taxSpecs = useMemo(() => {
    const key = modelKey(form.model);
    return taxonomy
      .filter(t => t.make === form.brand && modelKey(t.model) === key)
      .sort((a, b) => specLabel(a).localeCompare(specLabel(b), undefined, { sensitivity: 'base' }));
  }, [taxonomy, form.brand, form.model]);


  const applySpec = (id) => {
    const t = taxonomy.find(x => String(x.id) === String(id));
    if (!t) { set('taxonomy_match_id', ''); return; }
    setForm(f => ({ ...f,
      variant: t.variant || '', generation: t.generation || '',
      body_style: t.body_style || '', taxonomy_match_id: String(t.id) }));
  };

  const selectedSpecId = useMemo(() => {
    const hit = taxSpecs.find(t =>
      (t.variant || '') === (form.variant || '') &&
      (t.generation || '') === (form.generation || '') &&
      (t.body_style || '') === (form.body_style || ''));
    return hit ? String(hit.id) : '';
  }, [taxSpecs, form.variant, form.generation, form.body_style]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (vehicle) {
        await updateVehicle(vehicle.id, form);
      } else {
        await createVehicle(form);
      }
      onSave?.();
    } catch {}
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Registration Number *</label>
          <input className="form-input" required value={form.registration_number} onChange={e => set('registration_number', e.target.value)} placeholder="ABC-123" />
        </div>
        <div className="form-group">
          <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Brand *</span>
            <button type="button"
              onClick={() => { setFreeText(f => !f); setForm(v => ({ ...v, taxonomy_match_id: '' })); }}
              style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', padding: 0 }}>
              {freeText ? 'Choose from catalogue' : "Can't find it? Enter manually"}
            </button>
          </label>
          {freeText ? (
            <select className="form-input" value={form.brand} onChange={e => set('brand', e.target.value)} required>
              <option value="">Select brand…</option>
              {BRANDS.map(b => <option key={b}>{b}</option>)}
            </select>
          ) : (
            <select className="form-input" required value={form.brand}
              onChange={e => { setManualVariant(false); setForm(f => ({ ...f, brand: e.target.value, model: '', variant: '', generation: '', body_style: '', taxonomy_match_id: '' })); }}>
              <option value="">{taxLoading ? 'Loading…' : 'Select brand…'}</option>
              {taxMakes.map(b => <option key={b}>{b}</option>)}
            </select>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Model *</label>
          {freeText || !form.brand ? (
            <input className="form-input" required value={form.model}
              onChange={e => set('model', e.target.value)} placeholder="Defender 90" />
          ) : (
            <select className="form-input" required value={form.model}
              onChange={e => { setManualVariant(false); setForm(f => ({ ...f, model: e.target.value, variant: '', generation: '', body_style: '', taxonomy_match_id: '' })); }}>
              <option value="">Select model…</option>
              {taxModels.map(m => (
                <option key={m.label} value={m.label}>{m.label} ({m.listings.toLocaleString()})</option>
              ))}
            </select>
          )}
        </div>

        {!freeText && form.brand && form.model && taxSpecs.length > 0 && (
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Variant / Generation / Body</span>
              <button type="button"
                onClick={() => {
                  setManualVariant(m => !m);
                  setForm(f => ({ ...f, variant: '', generation: '', body_style: '', taxonomy_match_id: '' }));
                }}
                style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11, cursor: 'pointer', padding: 0 }}>
                {manualVariant ? 'Choose from catalogue' : "Can't find your variant? Enter manually"}
              </button>
            </label>
            {manualVariant ? (
              <>
                <input className="form-input" value={form.variant}
                  onChange={e => set('variant', e.target.value)} placeholder="Turbo, Roadster, HF Integrale 16V…" autoFocus />
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5, lineHeight: 1.5 }}>
                  Brand and model still match the catalogue for comparables — only the
                  variant is typed by hand, so add generation or body style in Description
                  if it helps narrow the valuation.
                </div>
              </>
            ) : (
              <>
                <select className="form-input" value={selectedSpecId} onChange={e => applySpec(e.target.value)}>
                  <option value="">Not sure — match on model only</option>
                  {taxSpecs.map(t => (
                    <option key={t.id} value={String(t.id)}>
                      {specLabel(t)} ({(t.listings || 0).toLocaleString()} comparables)
                    </option>
                  ))}
                </select>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5, lineHeight: 1.5 }}>
                  The number is how many sold listings back that exact specification — the
                  more there are, the more reliable the valuation.
                </div>
              </>
            )}
          </div>
        )}
        {!freeText && form.brand && form.model && taxSpecs.length === 0 && (
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label className="form-label">Variant</label>
            <input className="form-input" value={form.variant}
              onChange={e => set('variant', e.target.value)} placeholder="Turbo, Roadster, HF Integrale 16V…" />
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5, lineHeight: 1.5 }}>
              No catalogued variants for this model yet — type it in directly.
            </div>
          </div>
        )}

        {freeText && (
          <>
            <div className="form-group">
              <label className="form-label">Variant</label>
              <input className="form-input" value={form.variant}
                onChange={e => set('variant', e.target.value)} placeholder="Turbo, Roadster, HF Integrale 16V…" />
            </div>
            <div className="form-group">
              <label className="form-label">Body Style</label>
              <select className="form-input" value={form.body_style} onChange={e => set('body_style', e.target.value)}>
                <option value="">—</option>
                {['Coupe','Convertible','Roadster','Targa','Saloon','Estate','Hatchback','SUV','Pickup','Other']
                  .map(b => <option key={b}>{b}</option>)}
              </select>
            </div>
          </>
        )}

        <div className="form-group">
          <label className="form-label">Engine Size</label>
          <input className="form-input" value={form.engine_size}
            onChange={e => set('engine_size', e.target.value)} placeholder="3.3L Flat-6" />
        </div>
        <div className="form-group">
          <label className="form-label">Year</label>
          <input className="form-input" type="number" min="1950" max={new Date().getFullYear() + 1} value={form.year} onChange={e => set('year', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Color</label>
          <input className="form-input" value={form.color} onChange={e => set('color', e.target.value)} placeholder="White" />
        </div>
        <div className="form-group">
          <label className="form-label">Fuel Type</label>
          <select className="form-input" value={form.fuel_type} onChange={e => set('fuel_type', e.target.value)}>
            {['Petrol','Diesel','Electric','Hybrid','LPG','Other'].map(f => <option key={f}>{f}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">VIN / Chassis Number</label>
          <input className="form-input" value={form.vin} onChange={e => set('vin', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Engine Number</label>
          <input className="form-input" value={form.engine_number} onChange={e => set('engine_number', e.target.value)} />
        </div>
        <div>
          <MileageInput value={form.mileage} onChange={v => set('mileage', v)} label="Mileage" />
          <MileageMismatchNotice thisKm={form.mileage} otherKm={scoreKm}
            thisLabel="This form" otherLabel="the Collector Score (Current Mileage)" />
        </div>
        <div className="form-group">
          <label className="form-label">Ownership</label>
          <select className="form-input" value={form.ownership_status} onChange={e => set('ownership_status', e.target.value)}>
            {OWNERSHIP.map(o => <option key={o}>{o}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Current Location</label>
          <input className="form-input" maxLength={120} value={form.location} onChange={e => set('location', e.target.value)} placeholder="e.g. Limassol garage, bay 3" />
        </div>
        <div className="form-group">
          <label className="form-label">Status</label>
          <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="classic">Classic</option>
            <option value="restoration">Restoration</option>
            <option value="daneira">Company</option>
            <option value="immobilized">Immobilized</option>
            <option value="dokimi_test">Dealer Plates</option>
            <option value="service">Service</option>
            <option value="sold">Sold</option>
            <option value="scrapped">Scrapped</option>
          </select>
          {form.status === 'daneira' && (
            <div style={{ marginTop: 10 }}>
              <label className="form-label">Company Name</label>
              <input
                className="form-input"
                value={form.company_name || ''}
                onChange={e => set('company_name', e.target.value)}
                placeholder="Enter company name"
              />
            </div>
          )}
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)} />
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        {onCancel && <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>}
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving…' : vehicle ? 'Update Vehicle' : 'Add Vehicle'}
        </button>
      </div>
    </form>
  );
};